import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import './AIAssistant.css';

const AIAssistant = () => {
  // Load chat history from localStorage on mount
  const [messages, setMessages] = useState(() => {
    try {
      const saved = localStorage.getItem('ai-chat-history');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && parsed.length > 0) {
          console.log('📂 Loaded chat history from localStorage');
          return parsed;
        }
      }
    } catch (error) {
      console.error('Failed to load chat history:', error);
    }

    return [{
      role: 'assistant',
      content: '👋 Hi! I\'m Felix, your ArQive AI assistant. Ask me anything about LGBTQ+ history!'
    }];
  });

  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  // Keep a ref to always have the latest messages (avoids stale closure on unmount)
  const messagesRef = useRef(messages);
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // Save to localStorage whenever messages change
  useEffect(() => {
    try {
      localStorage.setItem('ai-chat-history', JSON.stringify(messages));
      console.log('💾 Saved chat history:', messages.length, 'messages');
    } catch (error) {
      console.error('❌ Failed to save chat history:', error);
    }
  }, [messages]);

  // Force save on unmount (handles the case where component is destroyed before last save)
  useEffect(() => {
    return () => {
      try {
        localStorage.setItem('ai-chat-history', JSON.stringify(messagesRef.current));
        console.log('💾 Saved on unmount');
      } catch (error) {
        console.error('Failed to save on unmount:', error);
      }
    };
  }, []);

  // Auto scroll to the latest message
  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(scrollToBottom, [messages]);

  // Send message to backend
  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = { role: 'user', content: input };
    const userInput = input;

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const apiUrl = `${process.env.REACT_APP_ARQIVE}/ai/chat/`;

      const response = await axios.post(
        apiUrl,
        { message: userInput },
        {
          headers: {
            'Content-Type': 'application/json',
            'X-Arqive-Api-Key': process.env.REACT_APP_API_KEY
          }
        }
      );

      if (response.data.success) {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: response.data.reply
        }]);
      } else {
        throw new Error(response.data.error || 'Unknown error');
      }
    } catch (error) {
      console.error('AI Error:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: '😔 Sorry, I encountered an error. Please try again in a moment.'
      }]);
    }

    setLoading(false);
  };

  // Clear chat history and reset to welcome message
  const clearHistory = () => {
    const initialMessage = {
      role: 'assistant',
      content: '👋 Hi! I\'m your ArQive AI assistant. Ask me anything about LGBTQ+ history!'
    };

    setMessages([initialMessage]);

    try {
      localStorage.setItem('ai-chat-history', JSON.stringify([initialMessage]));
      console.log('🗑️ Chat history cleared');
    } catch (error) {
      console.error('Failed to clear history:', error);
    }
  };

  // Send on Enter, allow Shift+Enter for newline
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(e);
    }
  };

  return (
    <div className="ai-assistant-container">
      <div className="ai-header">
        <div className="ai-header-content">
          <h2>🤖 Felix</h2>
          <p>Powered by Google Gemini</p>
        </div>
        <button
          onClick={clearHistory}
          className="clear-history-btn"
          title="Clear chat history"
        >
          🗑️
        </button>
      </div>

      <div className="ai-messages">
        {messages.map((msg, idx) => (
          <div key={idx} className={`message ${msg.role}`}>
            <div className="message-bubble">
              <div className="message-content">{msg.content}</div>
            </div>
          </div>
        ))}
        {loading && (
          <div className="message assistant">
            <div className="message-bubble">
              <div className="typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <form onSubmit={sendMessage} className="ai-input-form">
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Ask me about LGBTQ+ history..."
          disabled={loading}
          className="ai-input"
          rows="1"
        />
        <button
          type="submit"
          disabled={loading || !input.trim()}
          className="ai-send-btn"
        >
          {loading ? '⏳' : '📤'}
        </button>
      </form>
    </div>
  );
};

export default AIAssistant;